/*********************************************************************
* © Copyright IBM Corp. 2024
*********************************************************************/
import React, {useRef} from 'react'
import {useConfigurationAuditList} from './useConfigurationAuditList'
import {AuditEvent, AuditList} from '../ConfigurationList'
import {LayoutManagementClient, ServicesContextProvider} from './useServices'
import IConfigurator from 'tau/configuration'
import {UserService} from './services/UserService'
import {ProcessService} from './services/ProcessService'
import {GetDefaultTerms, GetTermsPerProcess, TermService} from './services/TermService'

export interface AuditEventPage {
  items: AuditEvent[]
  taken: number
  skipped: number
}

export interface ConfigurationAuditClient {
  getConfigurationChangeEvents(
    options: ConfigurationChangeEventsFiltersParameters
  ): Promise<AuditEventPage>
}

export interface ConfigurationAuditListProps {
  client: ConfigurationAuditClient
  initialOptions?: ConfigurationAuditSearchOptions
  eventTypeOptions?: string[]
  popoverZIndex?: number
  configurator: IConfigurator
}

export interface ConfigurationAuditSearchOptions {
  area?: string
  userId?: string
  eventType?: string
  createdAfter?: Date
  createdBefore?: Date
  take?: number
  skip?: number
}

export interface ConfigurationChangeEventsFiltersParameters {
  discriminators1?: string[]
  discriminators2?: string[]
  discriminators3?: string[]
  discriminators4?: string[]
  discriminators5?: string[]
  services?: string[]
  eventTypes?: string[]
  areas?: string[]
  userIds?: string[]
  createdAfter?: Date
  createdBefore?: Date
  take?: number
  skip?: number
}

export const ConfigurationAuditList = ({
  client,
  initialOptions,
  popoverZIndex,
  layoutManagementClient,
  configurator,
  getTermsPerProcess,
  getDefaultTerms
}: ConfigurationAuditListProps & {
  layoutManagementClient: LayoutManagementClient
  getTermsPerProcess: GetTermsPerProcess
  getDefaultTerms: GetDefaultTerms
}) => {
  const userService = useRef(new UserService({store2: configurator.getStore2()}))
  const processService = useRef(new ProcessService({store2: configurator.getStore2()}))
  const termService = useRef(new TermService(getTermsPerProcess, getDefaultTerms))
  const {
    auditEvents,
    options,
    onQueryChange,
    onUserIdChange,
    onClearFiltersClick,
    onAreaChange,
    loadMoreData,
    hasMoreData,
    onDateChange,
    hasNonEmptyFilter
  } = useConfigurationAuditList({
    client,
    userService: userService.current,
    processService: processService.current,
    initialOptions,
    termService: termService.current
  })

  const {eventType, createdAfter, createdBefore, userId, area} = options
  return (
    <ServicesContextProvider
      value={{
        layoutManagementClient
      }}
    >
      <AuditList
        popoverZIndex={popoverZIndex}
        auditEvents={auditEvents}
        query={eventType}
        area={area}
        userId={userId}
        afterDate={createdAfter}
        beforeDate={createdBefore}
        onUserIdChange={onUserIdChange}
        loadMoreData={loadMoreData}
        onAreaChange={onAreaChange}
        onQueryChange={onQueryChange}
        onDateChange={onDateChange}
        onClearFiltersClick={onClearFiltersClick}
        hasMoreData={hasMoreData}
        hasClearButton={hasNonEmptyFilter()}
      />
    </ServicesContextProvider>
  )
}
